// features/bx/billing/pages/BillingCycleSummary.tsx
import {
  useBillingSummary,
  useBillsList,
  useDistributionOverview,
  useExcludedConsumersList,
  useUpdateBillingConfiguration,
} from "@features/bx/BulkBillGeneration/hooks";
import { useDownloadBill, useViewBill } from "@features/bx/hooks";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { AlertBanner } from "@shared/components/CustomBanner";
import { GenericTable } from "@shared/components/GenericTable";
import { StepHelpers } from "@shared/components/Stepper";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { Switch } from "@shared/ui/switch";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  Activity,
  AlertCircle,
  ArrowDownCircle,
  ArrowUpCircle,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  MinusCircle,
  TrendingUp,
  Users,
} from "lucide-react";
import { useState } from "react";

import { Mail, Printer, Send } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const CHANNEL_META: Record<
  string,
  {
    icon: React.ElementType;
    iconClass: string;
    description: string;
  }
> = {
  email: {
    icon: Mail,
    iconClass: "text-blue-600",
    description: "Digital bill delivery via email",
  },
  paper: {
    icon: Printer,
    iconClass: "text-gray-600",
    description: "Physical bill delivery via postal mail",
  },
  both: {
    icon: Send,
    iconClass: "text-purple-600",
    description: "Dual delivery via email and postal mail",
  },
};

interface Step5ReviewBillsProps {
  selectedMeters?: string[];
  // Stepper-injected props
  onNext?: () => void;
  onPrevious?: () => void;
  currentStepIndex?: number;
  stepHelpers?: StepHelpers;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const Step5ReviewBills: React.FC<Step5ReviewBillsProps> = ({
  selectedMeters,
  onNext,
  onPrevious,
  currentStepIndex = 4,
  stepHelpers,
  setValidationError,
  clearValidationError,
  isValidating,
  isFirstStep,
  isLastStep,
}) => {
  // State for expandable sections
  const navigate = useNavigate();
  const [isConsumerBillsExpanded, setIsConsumerBillsExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isExcludedListExpanded, setIsExcludedListExpanded] = useState(true);
  const [isBillDistributionExpanded, setIsBillDistributionExpanded] =
    useState(true);
  const { id, cycleId, runId, action } = useParams();
  // State for distribution channels
  const [distributionChannels, setDistributionChannels] = useState({
    email: true,
    paper: true,
    both: true,
  });

  const { data: billDistributionData } = useDistributionOverview(runId);

  const { mutateAsync: updateBillingCycle, isPending: isUpdating } =
    useUpdateBillingConfiguration(id);

  const { data: billSummaryData } = useBillingSummary(runId);
  const currency = useCurrency();

  const consumptionDistribution = [
    {
      label: "Total Consumption",
      value: `${
        billSummaryData?.consumptionDistribution?.totalConsumption?.current ??
        "NA"
      } unit`,
      icon: <Activity className="w-6 h-6 text-blue-600" />,
    },
    {
      label: "High Consumption",
      value: `${
        billSummaryData?.consumptionDistribution?.highConsumption?.current ??
        "NA"
      } unit`,
      icon: <ArrowUpCircle className="w-6 h-6 text-orange-600" />,
    },
    {
      label: "Low Consumption",
      value: `${
        billSummaryData?.consumptionDistribution?.lowConsumption?.current ??
        "NA"
      } unit`,
      icon: <ArrowDownCircle className="w-6 h-6 text-purple-600" />,
    },
    {
      label: "Zero Consumption",
      value: `${
        billSummaryData?.consumptionDistribution?.zeroConsumption?.current ??
        "NA"
      } unit`,
      icon: <MinusCircle className="w-6 h-6 text-red-600" />,
    },
  ];

  const financialSummary = [
    {
      label: "Total Consumption",
      value: `${
        billSummaryData?.financialSummary?.totalConsumption?.current ?? "NA"
      } unit`,
      subtext: `Last: ${currency} ${
        billSummaryData?.financialSummary?.totalConsumption?.previous ?? "NA"
      } unit`,
      change: `${
        billSummaryData?.financialSummary?.totalConsumption?.changePercentage ??
        "NA"
      } from last cycle`,
      changeType: "increase" as const,
      icon: <Activity className="w-6 h-6 text-blue-600" />,
    },
    {
      label: "Total Billed Amount",
      value: `${currency} ${
        billSummaryData?.financialSummary?.totalBilledAmount?.current ?? "NA"
      }`,
      subtext: `Last: ${currency} ${
        billSummaryData?.financialSummary?.totalBilledAmount?.previous ?? "NA"
      } unit`,
      change: `${
        billSummaryData?.financialSummary?.totalBilledAmount
          ?.changePercentage ?? "NA"
      } from last cycle`,
      changeType: "increase" as const,
      icon: <FileText className="w-6 h-6 text-green-600" />,
    },
    {
      label: "Service Charges",
      value: `${currency} ${
        billSummaryData?.financialSummary?.serviceCharges?.current ?? "NA"
      }`,
      subtext: `Last: ${currency} ${
        billSummaryData?.financialSummary?.serviceCharges?.previous ?? "NA"
      } unit`,
      change: `${
        billSummaryData?.financialSummary?.serviceCharges?.changePercentage ??
        "NA"
      } from last cycle`,
      changeType: "increase" as const,
      icon: <TrendingUp className="w-6 h-6 text-purple-600" />,
    },
    {
      label: "Outstanding Amount",
      value:
        billSummaryData?.financialSummary?.outstandingAmount?.current ?? "NA",
      subtext: `Last: ${
        billSummaryData?.financialSummary?.outstandingAmount?.previous ?? "NA"
      } unit`,
      change: `${
        billSummaryData?.financialSummary?.outstandingAmount
          ?.changePercentage ?? "NA"
      } from last cycle`,
      changeType: "increase" as const,
      icon: <AlertCircle className="w-6 h-6 text-orange-600" />,
    },
    {
      label: "Late Payments",
      value: `${currency} ${
        billSummaryData?.financialSummary?.latePayments?.current ?? "NA"
      }`,
      subtext: `Last: ${currency}  ${
        billSummaryData?.financialSummary?.latePayments?.previous ?? "NA"
      } unit`,
      change: `${
        billSummaryData?.financialSummary?.latePayments?.changePercentage ??
        "NA"
      } from last cycle`,
      changeType: "increase" as const,
      icon: <Calendar className="w-6 h-6 text-red-600" />,
    },
  ];

  const billingSummaryKPIs = [
    {
      title: "Total Consumers",
      count: billSummaryData?.summaryCards?.totalConsumers ?? "NA",
      displayValue: billSummaryData?.summaryCards?.totalConsumers ?? "NA",
      // trendSummary: "4.5% vs Last",
      icon: <Users className="h-8 w-8 text-blue-500" />,
    },
    {
      title: "Bills Generated",
      count: billSummaryData?.summaryCards?.billsGenerated ?? "NA",
      displayValue: billSummaryData?.summaryCards?.billsGenerated ?? "NA",
      // trendSummary: "8.3% vs Last",
      icon: <FileText className="h-8 w-8 text-green-500" />,
    },
    {
      title: "Actual Bills",
      count: billSummaryData?.summaryCards?.actualBills ?? "NA",
      displayValue: billSummaryData?.summaryCards?.actualBills ?? "NA",
      // trendSummary: "4.8% vs Last",
      icon: <CheckCircle className="h-8 w-8 text-teal-500" />,
    },
    {
      title: "Estimated Bills",
      count: billSummaryData?.summaryCards?.estimatedBills ?? "NA",
      displayValue: billSummaryData?.summaryCards?.estimatedBills ?? "NA",
      // trendSummary: "45% vs Last",
      icon: <TrendingUp className="h-8 w-8 text-orange-500" />,
    },
    {
      title: "Unbilled",
      count: billSummaryData?.summaryCards?.unbilledConsumers ?? "NA",
      displayValue: billSummaryData?.summaryCards?.unbilledConsumers ?? "NA",
      // trendSummary: "68.3% vs Last",
      icon: <AlertCircle className="h-8 w-8 text-red-500" />,
    },
  ];

  // Hardcoded data - Consumer Bills
  const { data: consumerBillsData } = useBillsList(runId);

  const { data: excludedConsumersData } = useExcludedConsumersList(runId);

  const handleNext = async () => {
    onNext();
  };

  // Table columns - Consumer Bills
  const consumerBillsColumns: TableColumn<any>[] = [
    {
      key: "invoiceNo",
      header: "Invoice No",
      type: "text",
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      type: "text",
    },
    {
      key: "accountNumber",
      header: "Account Number",
      type: "text",
    },
    {
      key: "consumption",
      header: "Consumption",
      type: "text",
    },
    {
      key: "billAmount",
      header: "Bill Amount",
      type: "text",
    },
    {
      key: "outstanding",
      header: "Outstanding",
      type: "text",
      render: (value) => (
        <span
          className={value !== "$0.00" ? "text-orange-600 font-medium" : ""}
        >
          {value}
        </span>
      ),
    },
    {
      key: "totalDue",
      header: "Total Due",
      type: "text",
    },
    {
      key: "serviceDays",
      header: "Service Days",
      type: "text",
    },
  ];

  // Table columns - Excluded Consumers
  const excludedConsumersColumns: TableColumn<any>[] = [
    {
      key: "accountNo",
      header: "Account No",
      type: "text",
    },
    {
      key: "name",
      header: "Name",
      type: "text",
    },
    {
      key: "plan",
      header: "Plan",
      type: "text",
    },
    {
      key: "services",
      header: "Services",
      type: "text",
    },
    {
      key: "lastBillAmount",
      header: "Last Bill Amount",
      type: "text",
    },
    {
      key: "lastBillDate",
      header: "Last Bill Date",
      type: "text",
    },
    {
      key: "lastPaymentAmount",
      header: "Last Payment Amount",
      type: "text",
    },
    {
      key: "lastPaymentDate",
      header: "Last Payment Date",
      type: "text",
    },
  ];

  const downloadBill = useDownloadBill();
  const viewBill = useViewBill();
  const handleDownloadClick = async (billId: string, filename?: string) => {
    try {
      await downloadBill.mutateAsync({
        billId,
        filename: filename || `bill-${billId}.pdf`,
      });
      // showToast('Bill downloaded successfully', 'success');
    } catch (error) {
      console.error("Download failed:", error);
      // showToast('Failed to download the bill', 'error');
    }
  };

  const handleViewClick = async (billId: string) => {
    try {
      await viewBill.mutateAsync(billId);
    } catch (error) {
      console.error("View failed:", error);
      // showToast('Failed to view the bill', 'error');
    }
  };
  // Table actions
  const consumerBillsActions = [
    {
      label: "View",
      onClick: (row: any) => handleViewClick(row.id),
      icon: "Eye",
    },
    {
      label: "Download",
      onClick: (row: any) => handleDownloadClick(row.id),
      icon: "Download",
    },
  ];

  const handleDistributionToggle = (channel: string) => {
    console.log("Toggling channel:", channel);
    setDistributionChannels((prev) => ({
      ...prev,
      [channel]: !prev[channel as keyof typeof prev],
    }));
    console.log(
      `Toggle ${channel}:`,
      !distributionChannels[channel as keyof typeof distributionChannels],
    );
  };

  const handleSaveAsDraft = async () => {
    setIsLoading(true);

    const payload = {
      step: {
        step_working_on: 4,
        total_steps: 5,
      },
    };
    const response = await updateBillingCycle(payload);
    if (response) {
      navigate("/bx/create-bill-generation");
    }
  };

  return (
    <div>
      <div className="space-y-6">
        {/* Info Banner */}
        <AlertBanner
          variant="info"
          message="Review the complete billing summary including total amounts, number of bills generated, and any exceptions or errors that occurred."
        />

        {/* Billing Summary Section */}
        <div>
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Billing Summary
            </h2>
            <p className="text-sm text-gray-600">
              Summary of the bills generated for the billing cycle
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {billingSummaryKPIs.map((card, index) => (
              <QuickStatsCard
                key={index}
                title={card.title}
                count={card.count}
                displayValue={card.displayValue}
                // trendSummary={card.trendSummary}
                icon={card.icon}
              />
            ))}
          </div>
        </div>

        {/* Consumption Distribution Section */}
        <div>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Consumption Distribution
            </h3>
            <p className="text-sm text-gray-600">
              Overview of consumption patterns across consumers
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {consumptionDistribution.map((item, index) => (
              <QuickStatsCard
                key={index}
                title={item.label}
                count={item.value}
                icon={item.icon}
              />
            ))}
          </div>
        </div>

        {/* Financial Summary Section */}
        <div>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Financial Summary
            </h3>
            <p className="text-sm text-gray-600">
              Overview of billing financial metrics and late payments
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {financialSummary?.map((item, index) => (
              <QuickStatsCard
                key={index}
                title={item.label}
                count={item.value}
                trendSummary={`${item.subtext} • ${item.change}`}
                icon={item.icon}
              />
            ))}
          </div>
        </div>

        {billSummaryData?.revenueLeakageAlerts?.unbilledCount > 0 && (
          <AlertBanner
            variant="error"
            title="Revenue Leakage Alert"
            message={`${billSummaryData?.revenueLeakageAlerts?.unbilledCount} unbilled consumers detected with missing meter readings. Estimated revenue at risk ${currency} ${billSummaryData?.revenueLeakageAlerts?.estimatedRevenueAtRisk}. These consumers cannot be billed until their meter readings are collected or estimated. Review the Issues tab to identify and resolve missing readings.`}
            className="border-l-4"
          />
        )}

        {/* Consumer Bills Table - Expandable */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setIsConsumerBillsExpanded(!isConsumerBillsExpanded)}
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Consumer Bills
              </h3>
              <p className="text-sm text-gray-600">
                List of all consumers with bill details
              </p>
            </div>
            {isConsumerBillsExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-600" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-600" />
            )}
          </div>

          {isConsumerBillsExpanded && (
            <div className="p-4 pt-0">
              <AlertBanner
                variant="info"
                message="If there is any change in the bill template, please go and review the template configuration before proceeding."
                className="mb-4"
              />

              <GenericTable
                data={consumerBillsData?.result}
                columns={consumerBillsColumns}
                rowKey="id"
                actions={consumerBillsActions}
                actionsType="icons"
                emptyMessage="No consumer bills found"
                pagination={{
                  currentPage: consumerBillsData?.pageNumber,
                  totalPages: Math.ceil(consumerBillsData?.count / PAGE_SIZE),
                  totalItems: consumerBillsData?.count,
                  itemsPerPage: PAGE_SIZE,
                  startItem: 1,
                  endItem: Math.min(
                    consumerBillsData?.pageNumber * PAGE_SIZE,
                    consumerBillsData?.count,
                  ),
                }}
                onPageChange={(page) => console.log("Page changed:", page)}
              />
            </div>
          )}
        </div>

        {/* Excluded Consumer List - Expandable */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setIsExcludedListExpanded(!isExcludedListExpanded)}
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Excluded Consumer List
              </h3>
              <p className="text-sm text-gray-600">
                Consumers excluded from this billing cycle
              </p>
            </div>
            {isExcludedListExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-600" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-600" />
            )}
          </div>

          {isExcludedListExpanded && (
            <div className="p-4 pt-0">
              <GenericTable
                data={excludedConsumersData?.result}
                columns={excludedConsumersColumns}
                rowKey="id"
                emptyMessage="No excluded consumers"
                pagination={{
                  currentPage: 1,
                  totalPages: 1,
                  totalItems: excludedConsumersData?.count,
                  itemsPerPage: 10,
                  startItem: 1,
                  endItem: excludedConsumersData?.count,
                }}
                onPageChange={(page) => console.log("Page changed:", page)}
              />
            </div>
          )}
        </div>

        {/* Bill Distribution Overview - Expandable */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() =>
              setIsBillDistributionExpanded(!isBillDistributionExpanded)
            }
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Bill Distribution Overview
              </h3>
              <p className="text-sm text-gray-600">
                Distribution timeline and delivery details
              </p>
            </div>
            {isBillDistributionExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-600" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-600" />
            )}
          </div>

          {isBillDistributionExpanded && (
            <div className="p-4 pt-0 space-y-6">
              {/* Distribution Deadline */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                <Calendar className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Distribution Deadline
                  </p>
                  <p className="text-sm text-blue-700">
                    Bills must be distributed by:{" "}
                    <span className="font-semibold">January 15, 2025</span>
                  </p>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-xs text-gray-600 tracking-wide mb-1">
                    Total Bills
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mb-1">
                    {billDistributionData?.totalBills}
                  </p>
                  <p className="text-sm text-gray-600">
                    Ready for distribution
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 tracking-wide mb-1">
                    Distribution Method
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mb-1">
                    {billDistributionData?.distributionMethod}
                  </p>
                  <p className="text-sm text-gray-600">Multiple channels</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 tracking-wide mb-1">
                    Days Remaining
                  </p>
                  <p className="text-3xl font-bold text-red-600 mb-1">
                    {billDistributionData?.daysRemaining}
                  </p>
                  <p className="text-sm text-gray-600">Until deadline</p>
                </div>
              </div>

              {/* Distribution Channels */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">
                  Distribution Channels
                </h4>

                <div className="space-y-3">
                  {Object.entries(
                    billDistributionData?.distributionChannels ?? {},
                  ).map(([key, channel]) => {
                    const Icon = CHANNEL_META[key]?.icon;
                    const iconClass = CHANNEL_META[key]?.iconClass;
                    const description = CHANNEL_META[key]?.description;

                    return (
                      <div
                        key={key}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {Icon && <Icon className={`h-5 w-5 ${iconClass}`} />}
                          <div>
                            <p className="font-medium text-gray-900">
                              {channel.label}
                            </p>
                            <p className="text-sm text-gray-600">
                              {description}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-700">
                            {channel.consumerCount} consumers
                          </span>
                          <Switch
                            checked={distributionChannels[key]}
                            onCheckedChange={() =>
                              handleDistributionToggle(key)
                            }
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-6">
                  {/* {state.isCompleted && (
                          <Button variant="outline" onClick={exportLog}>
                            <FileDown className="mr-2 h-4 w-4" />
                            Export Log
                          </Button>
                        )} */}
                  <Button
                    variant="outline"
                    onClick={onPrevious}
                    disabled={isFirstStep}
                  >
                    Previous
                  </Button>
                  <div className="flex gap-4">
                    <Button onClick={handleSaveAsDraft}>Save As Draft</Button>

                    <Button onClick={handleNext}>Next</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step5ReviewBills;
